import logging from "utils/logging";
import React, { useEffect, useRef, useCallback } from "react";
import {
  FinalQuestionRevealerModal,
  MODAL_WIDTH,
  MODAL_HEIGHT,
} from "./FinalQuestionRevealerModal";
import { FinalRevealAdvanceFab } from "./FinalRevealAdvanceFab";
import { Typography, Box, Fade } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPlayerSubmissionsForFinalReveals,
  stopWatchingQuestionSubmissionsAction,
  watchQuestionSubmissionsAction,
} from "features/question/submissionSlice";
import {
  selectGameId,
  selectGameStatusObject,
  selectFinalRevealState,
} from "../../gamestate/gameStateSlice";
import { selectAllPlayers } from "../../player/playerSlice";
import {
  FINAL_REVEAL_STEP_PLAYER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
  FINAL_REVEAL_STEP_WAGER_REACTION,
  FINAL_REVEAL_STEP_STANDINGS,
  FINAL_REVEAL_STEP_WINNER,
} from "../../gamestate/constants";

const log = logging.getLogger("Final Round Podiums");

const GOLD = "#FFD700";

// Steps where the per-player modal should be open
const MODAL_STEPS = [
  FINAL_REVEAL_STEP_PLAYER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_INTRO,
  FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
  FINAL_REVEAL_STEP_WAGER_REACTION,
];

const Root = styled("div")({
  width: "100%",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
});

const HeaderSpacer = styled("div")({
  height: "130px",
});

const Header = styled(Box)({
  background: "transparent",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "125px",
  zIndex: 10,
});

const HeaderText = styled(Typography)({
  paddingTop: "16px",
  textAlign: "center",
  width: "100%",
  fontFamily: "Gyparody",
  color: GOLD,
  fontSize: "5em",
  textShadow: `0px 0px 0 rgb(243,179,0),
               1px 1px 0 rgb(230,166,0),
               2px 2px 0 rgb(218,154,0),
               3px 3px 0 rgb(206,142,0),
               4px 4px 0 rgb(193,129,0),
               5px 5px 0 rgb(181,117,0),
               6px 6px 0 rgb(168,104,0),
               7px 7px 0 rgb(156,92,0),
               8px 8px 0 rgb(144,80,0),
               9px 9px 8px rgba(120,128,204,0.58),
               9px 9px 1px rgba(120,128,204,0.5),
               0px 0px 8px rgba(120,128,204,.2)`,
});

const StandingsContainer = styled(Box)({
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  padding: "0 24px",
});

const PlayerRow = styled("div", {
  shouldForwardProp: (prop) =>
    !["isActive", "isRevealed", "isHighlighted"].includes(prop),
})(({ isActive, isRevealed, isHighlighted }) => ({
  display: "flex",
  alignItems: "center",
  padding: "10px 20px",
  marginBottom: "12px",
  borderRadius: "12px",
  background: isHighlighted
    ? "linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,215,0,0.08) 100%)"
    : isActive
      ? "rgba(255,215,0,0.1)"
      : "rgba(255,255,255,0.06)",
  border: isActive
    ? `2px solid ${GOLD}`
    : isHighlighted
      ? `2px solid rgba(255,215,0,0.4)`
      : "2px solid rgba(255,255,255,0.06)",
  backdropFilter: "blur(4px)",
  opacity: isRevealed || isActive || isHighlighted ? 1 : 0.55,
  transition: "all 0.4s ease",
  cursor: "default",
}));

const RankBadge = styled(Typography)({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "1.5em",
  color: GOLD,
  width: "50px",
  textAlign: "center",
  flexShrink: 0,
});

const SignatureThumbnail = styled("img")({
  width: "80px",
  height: "60px",
  objectFit: "contain",
  backgroundColor: "rgba(255,255,255,0.9)",
  borderRadius: "8px",
  marginRight: "16px",
  flexShrink: 0,
});

const SignaturePlaceholder = styled(Box)({
  width: "80px",
  height: "60px",
  backgroundColor: "rgba(255,255,255,0.1)",
  borderRadius: "8px",
  marginRight: "16px",
  flexShrink: 0,
});

const PlayerInfo = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const PlayerNameText = styled(Typography)({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 700,
  fontSize: "1.4em",
  color: "#ffffff",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

const PlayerSubtext = styled(Typography)({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "0.85em",
  color: "rgba(255,255,255,0.5)",
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

const ScoreText = styled(Typography)({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  fontSize: "1.5em",
  color: "#ffffff",
  flexShrink: 0,
  marginLeft: "16px",
});

const DeltaContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "50px",
  justifyContent: "center",
  flexShrink: 0,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Compute the display score for a player based on reveal status.
 */
const getDisplayScore = (player, isRevealed) => {
  if (isRevealed) {
    return player.finalScore ?? player.preFinalScore ?? 0;
  }
  return player.preFinalScore ?? player.score ?? 0;
};

/**
 * Sort players by their current display score (revealed = finalScore, others = preFinalScore).
 */
const sortByDisplayScore = (players, revealedIds) =>
  [...players].sort((a, b) => {
    const scoreA = getDisplayScore(a, revealedIds.has(a.id));
    const scoreB = getDisplayScore(b, revealedIds.has(b.id));
    return scoreB - scoreA;
  });

/**
 * Compute rank deltas between before and after the latest reveal.
 */
const computeDeltas = (players, revealOrder, revealedCount) => {
  if (revealedCount <= 0) return {};

  const beforeRevealed = new Set(revealOrder.slice(0, revealedCount - 1));
  const afterRevealed = new Set(revealOrder.slice(0, revealedCount));

  const before = sortByDisplayScore(players, beforeRevealed);
  const after = sortByDisplayScore(players, afterRevealed);

  const beforeRanks = {};
  before.forEach((p, i) => { beforeRanks[p.id] = i + 1; });
  const afterRanks = {};
  after.forEach((p, i) => { afterRanks[p.id] = i + 1; });

  const deltas = {};
  players.forEach((p) => {
    deltas[p.id] = (beforeRanks[p.id] ?? players.length) - (afterRanks[p.id] ?? players.length);
  });
  return deltas;
};

const DeltaArrow = ({ delta }) => {
  if (!delta || delta === 0) return <DeltaContainer />;
  if (delta > 0) {
    return (
      <DeltaContainer>
        <ArrowUpwardIcon sx={{ color: "#4caf50", fontSize: "1.1em" }} />
        <Typography sx={{ color: "#4caf50", fontWeight: 700, fontSize: "0.85em" }}>
          {delta}
        </Typography>
      </DeltaContainer>
    );
  }
  return (
    <DeltaContainer>
      <ArrowDownwardIcon sx={{ color: "#f44336", fontSize: "1.1em" }} />
      <Typography sx={{ color: "#f44336", fontWeight: 700, fontSize: "0.85em" }}>
        {Math.abs(delta)}
      </Typography>
    </DeltaContainer>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FinalRoundPodiums = () => {
  const dispatch = useDispatch();
  const rowRefs = useRef({});

  const gameStateObj = useSelector(selectGameStatusObject);
  const gameId = useSelector(selectGameId);
  const questionId = gameStateObj.questionId;
  const playerSubmissions = useSelector(selectPlayerSubmissionsForFinalReveals);
  const allPlayers = useSelector(selectAllPlayers);
  const finalReveal = useSelector(selectFinalRevealState);

  const currentStep = finalReveal?.currentStep || null;
  const currentPlayerId = finalReveal?.currentPlayerId || null;
  const revealOrder = finalReveal?.revealOrder || [];

  // Watch submissions for this question
  useEffect(() => {
    if (gameId && questionId) {
      dispatch(watchQuestionSubmissionsAction(gameId, questionId));
    }
    return () => {
      if (gameId && questionId) {
        dispatch(stopWatchingQuestionSubmissionsAction(gameId, questionId));
      }
    };
  }, [dispatch, questionId, gameId]);

  // Compute which players have been revealed (score committed)
  const revealedPlayerIds = new Set();
  if (revealOrder.length > 0 && currentPlayerId) {
    const currentIndex = revealOrder.indexOf(currentPlayerId);
    for (let i = 0; i < currentIndex; i++) {
      revealedPlayerIds.add(revealOrder[i]);
    }
    if (currentStep === FINAL_REVEAL_STEP_STANDINGS || currentStep === FINAL_REVEAL_STEP_WINNER) {
      revealedPlayerIds.add(currentPlayerId);
    }
  }

  const revealedCount = revealedPlayerIds.size;

  // Sort players by display score for the standings
  const sortedPlayers = playerSubmissions
    ? sortByDisplayScore(playerSubmissions, revealedPlayerIds)
    : [];

  // Compute deltas for the standings step
  const deltas = revealedCount > 0
    ? computeDeltas(allPlayers, revealOrder, revealedCount)
    : {};

  // Compute transform origin in the modal's local coordinate space so the
  // Zoom transition appears to grow out of the player's row. The modal is
  // centered in the viewport via flex, so its top-left sits at
  // ((vw - MODAL_WIDTH) / 2, (vh - MODAL_HEIGHT) / 2).
  const getTransformOrigin = useCallback(() => {
    if (!currentPlayerId || !rowRefs.current[currentPlayerId]) return null;
    const el = rowRefs.current[currentPlayerId];
    const rect = el.getBoundingClientRect();
    const rowCenterX = rect.left + rect.width / 2;
    const rowCenterY = rect.top + rect.height / 2;
    return {
      x: rowCenterX - (window.innerWidth - MODAL_WIDTH) / 2,
      y: rowCenterY - (window.innerHeight - MODAL_HEIGHT) / 2,
    };
  }, [currentPlayerId]);

  // Find the current player's data for the modal
  const currentPlayer = playerSubmissions?.find((p) => p.id === currentPlayerId);
  const currentSubmission = currentPlayer?.submission;

  const showModal = MODAL_STEPS.includes(currentStep);

  log.debug("FinalRoundPodiums render", {
    currentStep,
    currentPlayerId,
    showModal,
    revealedCount,
    playerCount: sortedPlayers.length,
  });

  return (
    <Root>
      <Header>
        <HeaderText variant="h1">Final Round</HeaderText>
      </Header>
      <HeaderSpacer />

      <StandingsContainer>
        {sortedPlayers.map((player, index) => {
          const isRevealed = revealedPlayerIds.has(player.id);
          const isActive = player.id === currentPlayerId && MODAL_STEPS.includes(currentStep);
          const isHighlighted = player.id === currentPlayerId &&
            (currentStep === FINAL_REVEAL_STEP_STANDINGS || currentStep === FINAL_REVEAL_STEP_WINNER);
          const score = getDisplayScore(player, isRevealed);

          return (
            <Fade in={true} timeout={400} key={player.id}>
              <PlayerRow
                ref={(el) => { rowRefs.current[player.id] = el; }}
                isActive={isActive}
                isRevealed={isRevealed}
                isHighlighted={isHighlighted}
              >
                <RankBadge>#{index + 1}</RankBadge>

                {player.signatureURL ? (
                  <SignatureThumbnail
                    src={player.signatureURL}
                    alt={`${player.name}'s signature`}
                  />
                ) : (
                  <SignaturePlaceholder />
                )}

                <PlayerInfo>
                  <PlayerNameText>{player.name}</PlayerNameText>
                  {isRevealed && (
                    <PlayerSubtext>
                      <CheckCircleOutlineIcon sx={{ fontSize: "0.9em", color: "rgba(255,255,255,0.4)" }} />
                      Revealed
                    </PlayerSubtext>
                  )}
                </PlayerInfo>

                <ScoreText>${score.toLocaleString()}</ScoreText>

                <DeltaArrow delta={deltas[player.id]} />
              </PlayerRow>
            </Fade>
          );
        })}
      </StandingsContainer>

      <FinalQuestionRevealerModal
        open={showModal}
        player={currentPlayer}
        submission={currentSubmission}
        currentStep={currentStep}
        transformOrigin={showModal ? getTransformOrigin() : null}
        onClose={() => {}}
      />

      <FinalRevealAdvanceFab />
    </Root>
  );
};

export { FinalRoundPodiums };
